import { INTERNAL_ROLES } from '@croissants/contracts/enums';
import { endPilotSession, probePilotSession } from '@croissants/contracts/pilot';
import {
  Button,
  Card,
  clerkScriptReady,
  consumeSearchParam,
  FormField,
  Spinner,
  TextInput,
  Wordmark,
} from '@croissants/ui';
import {
  createContext,
  type FormEvent,
  type JSX,
  lazy,
  type ReactNode,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'wouter';
import { useT } from '../i18n/index';
import { config } from './env';
import { rememberDestination, takeDestination } from './return-to';

/**
 * Authentication for the internal portal.
 *
 * `/internal` is served by its own Clerk application, sharing no user pool with the
 * partner or bakery portals - a blast-radius decision (tech-stack section 7).
 * That application is the Google one the pilot gate already used: `internal.`
 * gates itself now that the launcher lives inside it, and the second factor is
 * the Google account's own (plan website-app-split section 4). This module
 * exposes one session shape to the rest of the app so no screen knows which
 * mechanism produced the credentials.
 *
 * An AGENT's signed `cib_pilot` cookie is the second way in, and it is a FALLBACK
 * rather than a mode: the gate asks for one only when Clerk reports nobody
 * signed in. That is what keeps `GET /pilot/agent` - the headless-agent bypass,
 * which is how every automated session reaches these portals - working on a
 * portal whose interactive sign-in is allowlisted to two humans. The cookie the
 * Pilot tab mints for a PERSON is not one (founder, 2026-09-20): it opens the
 * demo tenants, and on this portal a person is only ever their Google account.
 *
 * The development fallback speaks the API's `X-Dev-Role` header bypass, which the
 * worker hard-refuses in production whatever DEV_AUTH says. It is therefore only
 * offered on a development build and never on a deployed one: `readConfig()`
 * reports `unconfigured` instead, and the gate below says exactly which variable
 * is missing rather than failing with an opaque Clerk error at 05:45.
 */

export type AuthState = 'loading' | 'signed-out' | 'signed-in';

export interface InternalSession {
  readonly state: AuthState;
  /**
   * What produced these credentials - which is not the same question as which
   * sign-in this build OFFERS (`config.authMode`). A build offering Clerk still
   * runs on an `agent` session when the browser arrived holding an agent's
   * cookie.
   */
  readonly mode: 'clerk' | 'dev' | 'agent' | 'unconfigured';
  /** Who is signed in, for the shell. Null while signed out. */
  readonly label: string | null;
  /** The provider's picture - the Google avatar on a federated sign-in. */
  readonly imageUrl: string | null;
  /** Opens the provider's account screen; null when no provider is behind it. */
  readonly openAccount: (() => void) | null;
  /** Credentials for one API call. Re-read per request so a token refresh lands. */
  authHeaders(): Promise<Record<string, string>>;
  signOut(): void;
}

/** Exported for `auth-clerk.tsx`, the lazily-loaded Clerk half of this module. */
export const SessionContext = createContext<InternalSession | null>(null);

export function useSession(): InternalSession {
  const session = useContext(SessionContext);
  if (session === null) throw new Error('useSession must be used inside <AuthProvider>');
  return session;
}

// ---------------------------------------------------------------------------
// Clerk-backed session
// ---------------------------------------------------------------------------

/**
 * The Clerk half lives in `auth-clerk.tsx` and loads only when the configured
 * mode is 'clerk': pilot and dev builds never download `@clerk/clerk-react` or
 * its dependencies at all (~17% of the initial bundle).
 */
type ClerkModule = typeof import('./auth-clerk');
let clerkModule: Promise<ClerkModule> | null = null;

/**
 * The Clerk half, resolved only once Clerk's own script has run as well: the
 * provider then finds `window.Clerk` and calls `load()` at once, instead of on
 * its next 100 ms poll (`clerkScriptReady`, measured 81-118 ms on every load).
 */
function loadClerkModule(): Promise<ClerkModule> {
  clerkModule ??= Promise.all([import('./auth-clerk'), clerkScriptReady()]).then(
    ([module]) => module,
  );
  return clerkModule;
}

// Started with this module rather than at the first render, so the chunk
// downloads while the rest of the bundle runs.
if (
  typeof document !== 'undefined' &&
  config.authMode === 'clerk' &&
  config.clerkPublishableKey !== null
) {
  loadClerkModule().catch(() => undefined);
}

/**
 * The Clerk half, or null while it loads - held in state rather than behind
 * `lazy()` and `<Suspense>` on purpose. A Suspense boundary that has shown its
 * fallback reveals its content no sooner than 300 ms later (React's
 * FALLBACK_THROTTLE_MS), so every screen's first request waited for that
 * timer whenever Clerk answered sooner. A failure is thrown into the render,
 * as a rejected `lazy()` was, for the nearest error boundary.
 */
function useClerkModule(): ClerkModule | null {
  const [state, setState] = useState<{ module: ClerkModule | null; failure: unknown }>({
    module: null,
    failure: null,
  });
  useEffect(() => {
    let live = true;
    loadClerkModule().then(
      (module) => {
        if (live) setState({ module, failure: null });
      },
      (failure: unknown) => {
        if (live) setState({ module: null, failure: failure ?? new Error('Clerk failed to load') });
      },
    );
    return () => {
      live = false;
    };
  }, []);
  if (state.failure !== null) throw state.failure;
  return state.module;
}

function ClerkAuth({
  publishableKey,
  children,
}: {
  publishableKey: string;
  children: ReactNode;
}): JSX.Element {
  const clerk = useClerkModule();
  if (clerk === null) {
    // The same "loading" session the bridge itself starts in, so the gate
    // shows one continuous loading state rather than a flash of anything else.
    return (
      <SessionContext.Provider value={{ ...UNCONFIGURED_SESSION, state: 'loading', mode: 'clerk' }}>
        {children}
      </SessionContext.Provider>
    );
  }
  return (
    <clerk.ClerkAuthProvider publishableKey={publishableKey}>{children}</clerk.ClerkAuthProvider>
  );
}

const ClerkSignInPanel = lazy(async () => ({
  default: (await import('./auth-clerk')).ClerkSignInPanel,
}));

// ---------------------------------------------------------------------------
// Development session - the API's documented header bypass
// ---------------------------------------------------------------------------

const DEV_STORAGE_KEY = 'cib.internal.devAuth';

/** The staff roles a development session may take: every internal role. */
export const DEV_ROLES = INTERNAL_ROLES;
export type DevRole = (typeof DEV_ROLES)[number];

interface DevIdentity {
  readonly role: DevRole;
  readonly orgId: string;
}

function readDevIdentity(): DevIdentity | null {
  try {
    const raw = globalThis.localStorage?.getItem(DEV_STORAGE_KEY);
    if (raw === null || raw === undefined) return null;
    const parsed = JSON.parse(raw) as Partial<DevIdentity>;
    if (typeof parsed.role !== 'string') return null;
    if (!(DEV_ROLES as readonly string[]).includes(parsed.role)) return null;
    return { role: parsed.role as DevRole, orgId: parsed.orgId ?? '' };
  } catch {
    return null;
  }
}

interface DevSessionApi {
  readonly identity: DevIdentity | null;
  signIn(identity: DevIdentity): void;
}

const DevSessionContext = createContext<DevSessionApi | null>(null);

/**
 * A development role a LINK may carry (`?dev=INTERNAL_ADMIN`), so
 * `scripts/dev-preview.mjs` opens this portal signed in rather than on the
 * development form - the stored identity is per origin, and every preview is a
 * new random port. Consumed once: written, then stripped from the address bar
 * (`consumeSearchParam`, `@croissants/ui`). Only a listed role word is accepted; the value becomes a request header.
 */
export function requestedDevRole(search: string): DevRole | null {
  if (search === '') return null;
  const asked = new URLSearchParams(search).get('dev');
  if (asked === null || !(DEV_ROLES as readonly string[]).includes(asked)) return null;
  return asked as DevRole;
}

function consumeRequestedDevIdentity(): DevIdentity | null {
  const role = consumeSearchParam('dev', requestedDevRole);
  if (role === null) return null;
  const identity: DevIdentity = { role, orgId: '' };
  try {
    globalThis.localStorage?.setItem(DEV_STORAGE_KEY, JSON.stringify(identity));
  } catch {
    // Still signed in for this tab.
  }
  return identity;
}

function DevSessionBridge({ children }: { children: ReactNode }): JSX.Element {
  const [identity, setIdentity] = useState<DevIdentity | null>(
    () => consumeRequestedDevIdentity() ?? readDevIdentity(),
  );

  const signIn = useCallback((next: DevIdentity) => {
    setIdentity(next);
    try {
      globalThis.localStorage?.setItem(DEV_STORAGE_KEY, JSON.stringify(next));
    } catch {
      // A session that cannot be persisted still works for this tab.
    }
  }, []);

  const signOut = useCallback(() => {
    setIdentity(null);
    try {
      globalThis.localStorage?.removeItem(DEV_STORAGE_KEY);
    } catch {
      // Nothing to do: the in-memory state is already cleared.
    }
  }, []);

  const session = useMemo<InternalSession>(
    () => ({
      state: identity === null ? 'signed-out' : 'signed-in',
      mode: 'dev',
      label: identity === null ? null : `${identity.role}`,
      imageUrl: null,
      openAccount: null,
      authHeaders: async (): Promise<Record<string, string>> =>
        identity === null
          ? {}
          : {
              'x-dev-role': identity.role,
              ...(identity.orgId === '' ? {} : { 'x-dev-org': identity.orgId }),
            },
      signOut,
    }),
    [identity, signOut],
  );

  const devApi = useMemo<DevSessionApi>(() => ({ identity, signIn }), [identity, signIn]);

  return (
    <DevSessionContext.Provider value={devApi}>
      <SessionContext.Provider value={session}>{children}</SessionContext.Provider>
    </DevSessionContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Pilot session - the shared `cib_pilot` cookie (docs/pilot-access.html)
// ---------------------------------------------------------------------------

/**
 * Is this browser an agent's - holding a cookie that opens Internal?
 *
 * `opensInternal` is the worker's own answer (`pilotCookieOpens`), so the gate
 * here and the acceptor there cannot disagree: a person's launcher cookie reads
 * as no session, and the Google sign-in is what they see.
 *
 * The cookie is HttpOnly, so nothing here can read it: the question has to be
 * asked of the API, and `probePilotSession` is that question. Asked ONLY when
 * Clerk has reported nobody signed in, so an operator with a live Clerk session
 * pays nothing for it, and asked once.
 *
 * `'idle'` is the answer when nothing asked - a development build, or a build
 * with no API address. It reads as "no session" at the call site, which is
 * right: the dev header form is the way in there.
 */
type PilotProbe = 'idle' | 'loading' | 'session' | 'none';

function usePilotCookie(ask: boolean): PilotProbe {
  const [answer, setAnswer] = useState<PilotProbe>('idle');

  useEffect(() => {
    if (!ask) return;
    let live = true;
    void probePilotSession(config.apiBaseUrl).then((result) => {
      if (live) setAnswer(result?.opensInternal === true ? 'session' : 'none');
    });
    return () => {
      live = false;
    };
  }, [ask]);

  // Before the effect has answered, the honest state is "asking" - returning
  // 'none' for that one render would flash the sign-in panel at a browser that
  // turns out to be signed in.
  if (!ask) return 'idle';
  return answer === 'idle' ? 'loading' : answer;
}

/**
 * The session a valid cookie is.
 *
 * No headers are contributed at all. The credential IS the cookie, and the API
 * client is what carries it (`pilotCookie: true` in `api.tsx`, applied by
 * `pilotCookieCredentials` in packages/ui/src/transport.ts, the one statement of
 * that policy); a bearer header here would be a second, contradictory
 * credential on the same request.
 *
 * "Agent": only an agent's cookie reaches this, and the account menu says so
 * rather than wearing the name of the founder's own launcher.
 */
function AgentSessionProvider({ children }: { children: ReactNode }): JSX.Element {
  const session = useMemo<InternalSession>(
    () => ({
      state: 'signed-in',
      mode: 'agent',
      label: 'Agent',
      imageUrl: null,
      openAccount: null,
      authHeaders: async () => ({}),
      signOut: () => {
        // One cookie opens four portals, so ending it ends all four - which is
        // why the Pilot screen says so beside the same action. Reloading is what
        // sends this portal back through the gate.
        void endPilotSession(config.apiBaseUrl).then(() => globalThis.location?.reload());
      },
    }),
    [],
  );

  return <SessionContext.Provider value={session}>{children}</SessionContext.Provider>;
}

// ---------------------------------------------------------------------------
// Unconfigured build - fail loudly and say which variable is missing
// ---------------------------------------------------------------------------

const UNCONFIGURED_SESSION: InternalSession = {
  state: 'signed-out',
  mode: 'unconfigured',
  label: null,
  imageUrl: null,
  openAccount: null,
  authHeaders: async () => ({}),
  signOut: () => {},
};

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  if (config.authMode === 'clerk' && config.clerkPublishableKey !== null) {
    return <ClerkAuth publishableKey={config.clerkPublishableKey}>{children}</ClerkAuth>;
  }
  if (config.authMode === 'dev') {
    return <DevSessionBridge>{children}</DevSessionBridge>;
  }
  return <SessionContext.Provider value={UNCONFIGURED_SESSION}>{children}</SessionContext.Provider>;
}

// ---------------------------------------------------------------------------
// The gate
// ---------------------------------------------------------------------------

function SignInFrame({ children }: { children: ReactNode }): JSX.Element {
  return (
    <main className="cib-root internal-signin">
      <div className="internal-signin__panel">
        <Wordmark size={44} />
        {children}
      </div>
    </main>
  );
}

function DevSignInForm(): JSX.Element {
  const t = useT();
  const devApi = useContext(DevSessionContext);
  const [role, setRole] = useState<DevRole>('INTERNAL_ADMIN');
  const [orgId, setOrgId] = useState('');

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    devApi?.signIn({ role, orgId: orgId.trim() });
  };

  return (
    <Card title={t('auth.dev.title')}>
      <p className="internal-callout internal-callout--warning" role="note">
        {t('auth.dev.warning')}
      </p>
      <form onSubmit={submit}>
        <FormField label={t('auth.dev.role')} hint={t('auth.dev.role.hint')} required>
          {(control) => (
            <select
              {...control}
              value={role}
              onChange={(event) => setRole(event.target.value as DevRole)}
            >
              {DEV_ROLES.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          )}
        </FormField>
        <FormField label={t('auth.dev.org')} hint={t('auth.dev.org.hint')}>
          {(control) => (
            <TextInput
              {...control}
              value={orgId}
              onChange={(event) => setOrgId(event.target.value)}
              autoComplete="off"
            />
          )}
        </FormField>
        <div className="internal-actions">
          <Button type="submit">{t('auth.dev.submit')}</Button>
        </div>
      </form>
    </Card>
  );
}

/** The path an operator asked for, query and fragment included. */
function requestedPath(): string {
  const location = globalThis.location;
  if (location === undefined) return '/';
  return `${location.pathname}${location.search}${location.hash}`;
}

/**
 * Renders `children` only for a signed-in operator. Everything else - loading,
 * the cookie fallback, the sign-in surface and a misconfigured build - is
 * handled here, so no screen has to consider an unauthenticated state.
 *
 * Two things happen around the sign-in that are not the sign-in itself.
 *
 * The requested path is REMEMBERED the moment a sign-in surface is shown and
 * restored the moment one succeeds (`return-to.ts`). A Google round trip comes
 * back on whatever URL the identity provider was given, which is not the one
 * that was typed; without this, `internal./pilot` typed cold lands on the
 * dashboard, and `/pilot` is the only way into the other three portals.
 *
 * And before any sign-in is offered, the API is asked whether this browser
 * already holds a pilot cookie. That is the headless-agent path (`GET
 * /pilot/agent`): the interactive sign-in is allowlisted to two humans, so
 * without it no automated session could open this portal at all. Not on the
 * production instance, where the gate does not exist: the question could only be
 * answered "no" there, and asking it would suggest the answer might be yes.
 */
export function SignInGate({ children }: { children: ReactNode }): JSX.Element {
  const t = useT();
  const session = useSession();
  const [, navigate] = useLocation();

  const signedOut = session.state === 'signed-out';
  // Not on a development build: there the header form is the way in, and a probe
  // would only delay it. Not without an API address either - there is nothing to
  // ask. And not on the production instance, where `/pilot/context` 404s by
  // design and the cookie is refused on every mount anyway.
  const probe = usePilotCookie(
    signedOut && config.apiConfigured && config.authMode !== 'dev' && !config.isProductionInstance,
  );
  const showingSignIn = signedOut && probe !== 'loading' && probe !== 'session';

  useEffect(() => {
    if (showingSignIn) rememberDestination(requestedPath());
  }, [showingSignIn]);

  useEffect(() => {
    if (session.state !== 'signed-in') return;
    const destination = takeDestination();
    if (destination !== null && destination !== requestedPath()) {
      navigate(destination, { replace: true });
    }
  }, [session.state, navigate]);

  if (!config.apiConfigured) {
    return (
      <SignInFrame>
        <Card title={t('auth.apiMissing.title')}>
          <p>{t('auth.apiMissing.body')}</p>
        </Card>
      </SignInFrame>
    );
  }

  if (session.state === 'loading' || probe === 'loading') {
    return (
      <SignInFrame>
        <p className="internal-inline">
          <Spinner label={t('auth.signIn.loading')} />
          {t('auth.signIn.loading')}
        </p>
      </SignInFrame>
    );
  }

  // A cookie this deployment signed is a session, whatever this build's own
  // sign-in would have been - including on a build that has no usable key and
  // would otherwise only be able to name the missing variable.
  if (probe === 'session') {
    return <AgentSessionProvider>{children}</AgentSessionProvider>;
  }

  if (signedOut) {
    return (
      <SignInFrame>
        {session.mode === 'unconfigured' ? (
          <Card title={t('auth.unconfigured.title')}>
            <p>{t('auth.unconfigured.body')}</p>
          </Card>
        ) : session.mode === 'dev' ? (
          <DevSignInForm />
        ) : (
          // Clerk's own panel already carries the heading and the Google button;
          // a title and a paragraph above it restated both and named a user-pool
          // boundary nobody signing in has to think about.
          <Suspense fallback={<Spinner label={t('auth.signIn.loading')} />}>
            <ClerkSignInPanel />
          </Suspense>
        )}
      </SignInFrame>
    );
  }

  return <>{children}</>;
}
